import { translate } from "app/filters/i18nFilter";
import type { Mutable } from "app/helpers/types";
import type {
    ChipGroupItem,
} from "app/pages/sales-intelligence/common-components/filters-panel/components/ChipsGroup/ChipsGroup";
import type {
    TechnologiesFilterDto,
    WithTechnologiesCount,
} from "app/pages/sales-intelligence/domain/technology/types/filter-values";
import type {
    GroupedTechnologies,
    Technology,
    TechnologyCategory,
    TechnologySubCategory,
} from "app/pages/sales-intelligence/domain/technology/types/model";
import { compareNamesAsc } from "app/pages/sales-intelligence/sub-modules/advanced-search/helpers/common";
import type { TabbedDropdownItemType } from "app/pages/sales-intelligence/types/types";

import type { DisabledDropdownTechnologyValues } from "../types/dropdown";
import {
    type CategoryListItemType,
    type SubCategoryListItemType,
    TechnologiesConditionTypeEnum,
    TechnologiesContentEnum,
    type TechnologiesListItems,
    type TechnologyListItemType,
} from "../types/technologies";

/** @deprecated import from "app/pages/sales-intelligence/domain/technology/utils/grouping" */
export { groupTechnologiesByType } from "app/pages/sales-intelligence/domain/technology/utils/grouping";

/** @deprecated import from "app/pages/sales-intelligence/domain/technology/utils/operations" */
export { hasSameTechnologyObject } from "app/pages/sales-intelligence/domain/technology/utils/operations";

export const getTechnologiesDDItem = (
    item: Technology,
    disabledValues: DisabledDropdownTechnologyValues,
    technologyItem: TechnologiesFilterDto,
): Pick<TabbedDropdownItemType, "name" | "type" | "isSelected" | "isDisabled" | "secondaryName"> &
    WithTechnologiesCount => {
    const isDisabledBySubCategory = disabledValues.categories.includes(item.secondaryName);
    const isDisabledByCategory = disabledValues.parentCategories.includes(item.secondaryName);
    const isSelected = technologyItem.technologies.includes(item.name);
    return {
        name: item.name,
        type: TechnologiesContentEnum.Technologies,
        secondaryName: item.secondaryName,
        isSelected: isDisabledByCategory || isDisabledBySubCategory || isSelected,
        isDisabled: (isDisabledByCategory || isDisabledBySubCategory) && !isSelected,
    };
};

export const getSubCategoriesDDItem = (
    item: TechnologySubCategory,
    disabledValues: DisabledDropdownTechnologyValues,
    technologyItem: TechnologiesFilterDto,
): Pick<TabbedDropdownItemType, "name" | "type" | "isSelected" | "isDisabled" | "secondaryName"> &
    WithTechnologiesCount => {
    const isDisabledByCategory = disabledValues.parentCategories.includes(item.secondaryName);
    const isSelected = technologyItem.categories.includes(item.name);
    return {
        name: item.name,
        type: TechnologiesContentEnum.SubCategory,
        technologiesCount: item.techCount,
        secondaryName: item.secondaryName,
        isSelected: isDisabledByCategory || isSelected,
        isDisabled: isDisabledByCategory && !isSelected,
    };
};

export const getCategoriesDDItem = (
    item: TechnologyCategory,
    disabledValues: DisabledDropdownTechnologyValues,
    technologyItem: TechnologiesFilterDto,
): Pick<TabbedDropdownItemType, "name" | "type" | "isSelected" | "isDisabled"> & WithTechnologiesCount => {
    const isSelected = technologyItem.parentCategories.includes(item.name);
    const isDisabled = disabledValues.parentCategories.includes(item.name);
    return {
        name: item.name,
        type: TechnologiesContentEnum.Category,
        technologiesCount: item.techCount,
        isSelected: isDisabled || isSelected,
        isDisabled: isDisabled && !isSelected,
    };
};

export const getDisabledTechnologyValues = (
    items: TechnologiesFilterDto[],
    selected: TechnologiesFilterDto | null,
): DisabledDropdownTechnologyValues => {
    return items.reduce(
        (acc: DisabledDropdownTechnologyValues, item: TechnologiesFilterDto) => {
            const filteredParentCategories = selected
                ? item.parentCategories.filter((category) => !selected.parentCategories.includes(category))
                : item.parentCategories;

            const filteredCategories = selected
                ? item.categories.filter((category) => selected.categories.includes(category))
                : item.categories;
            const filteredTechnologies = selected
                ? item.technologies.filter((technology) => selected.technologies.includes(technology))
                : item.technologies;
            acc.parentCategories.push(...filteredParentCategories);
            acc.categories.push(...filteredCategories);
            acc.technologies.push(...filteredTechnologies);
            return acc;
        },
        { parentCategories: [], categories: [], technologies: [] },
    );
};

const toggle = (arr: readonly string[], value: string): string[] =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

const manageTechnologyType = (arr: readonly string[], type: TechnologiesContentEnum) => {
    return arr.map((v: string) => ({ name: v, type }));
};

export const updateTechnologyItem = (
    prev: TechnologiesFilterDto,
    item: { name: string; secondaryName?: string; type?: string },
    options: {
        technologies: Technology[];
        subCategories: TechnologySubCategory[];
    },
): TechnologiesFilterDto => {
    const next = { ...prev };

    if (item.type) {
        switch (item.type) {
            case TechnologiesContentEnum.Category:
                next.parentCategories = toggle(prev.parentCategories, item.name);
                break;
            case TechnologiesContentEnum.SubCategory:
                next.categories = toggle(prev.categories, item.name);
                break;
            case TechnologiesContentEnum.Technologies:
                next.technologies = toggle(prev.technologies, item.name);
                break;
        }
    } else if (!item.secondaryName) {
        next.parentCategories = toggle(prev.parentCategories, item.name);
    } else {
        const isTech = options.technologies.some((t) => t.name === item.name && t.secondaryName === item.secondaryName);

        if (isTech) {
            next.technologies = toggle(prev.technologies, item.name);
        } else {
            next.categories = toggle(prev.categories, item.name);
        }
    }

    return next;
};

export const manageTechnologyChipsItemByType = <T>(
    item: ChipGroupItem<T>,
    prev: TechnologiesFilterDto,
): TechnologiesFilterDto => {
    const next: Mutable<TechnologiesFilterDto> = { ...prev };

    switch (item.type) {
        case TechnologiesContentEnum.Category:
            next.parentCategories = toggle(prev.parentCategories, item.name);
            break;
        case TechnologiesContentEnum.SubCategory:
            next.categories = toggle(prev.categories, item.name);
            break;
        case TechnologiesContentEnum.Technologies:
            next.technologies = toggle(prev.technologies, item.name);
            break;
    }
    return next;
};

export const getTechnologyChipsData = (item: TechnologiesFilterDto) => {
    return [
        ...manageTechnologyType(item.parentCategories, TechnologiesContentEnum.Category),
        ...manageTechnologyType(item.categories, TechnologiesContentEnum.SubCategory),
        ...manageTechnologyType(item.technologies, TechnologiesContentEnum.Technologies),
    ];
};

export const getTechnologyTabs = (
    options: GroupedTechnologies,
    disabledValues: DisabledDropdownTechnologyValues,
    technologyItem: TechnologiesFilterDto,
) => {
    const technologyTab = {
        id: "technology-tab",
        name: translate("si.lead_gen_filters.filters.technologies.tabs.technologies-names", {
            amountOfItems: options.technologies.length,
        }),
        items: options.technologies.map((item: Technology) => {
            return getTechnologiesDDItem(item, disabledValues, technologyItem);
        }),
    };

    const subCategoryTab = {
        id: "sub-category-tab",
        name: translate("si.lead_gen_filters.filters.technologies.tabs.sub-categories-names", {
            amountOfItems: options.subCategories.length,
        }),
        items: options.subCategories.map((item: TechnologySubCategory) => {
            return getSubCategoriesDDItem(item, disabledValues, technologyItem);
        }),
    };

    const categoryTab = {
        id: "category-tab",
        name: translate("si.lead_gen_filters.filters.technologies.tabs.categories-names", {
            amountOfItems: options.categories.length,
        }),
        items: options.categories.map((item: TechnologyCategory) => {
            return getCategoriesDDItem(item, disabledValues, technologyItem);
        }),
    };

    return [categoryTab, subCategoryTab, technologyTab];
};

export function getTechnologiesListItems(groupedTechnologies: GroupedTechnologies): TechnologiesListItems {
    const categories: CategoryListItemType[] = groupedTechnologies.categories.map((category) => ({
        name: category.name,
        technologiesCount: category.techCount,
        type: TechnologiesConditionTypeEnum.Category,
    }));

    const subCategories: SubCategoryListItemType[] = groupedTechnologies.subCategories.map(
        ({ name, secondaryName, techCount }) => ({
            name,
            secondaryName,
            technologiesCount: techCount,
            type: TechnologiesConditionTypeEnum.SubCategory,
        }),
    );

    const technologies: TechnologyListItemType[] = groupedTechnologies.technologies.map((tech) => ({
        ...tech,
        type: TechnologiesConditionTypeEnum.Technology,
    }));

    return {
        categories,
        technologies: technologies.sort(compareNamesAsc),
        subCategories: subCategories.sort(compareNamesAsc),
    };
}
